import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { AuthService } from '../../shared/service/auth-service';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToasterCommonService } from '../../shared/service/toaster-common-service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ThemeService } from '../../shared/service/theme-service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-sign-up',
  standalone: false,
  templateUrl: './sign-up.html',
  styleUrl: './sign-up.scss'
})
export class SignUp implements OnInit {
  signupForm!: FormGroup;
  password_show_and_hide = false;
  loading = false;
  currentYear: number = new Date().getFullYear();
  plans: any[] = [];
  selectedPlan: any = null;
  signupSuccess = false;
  public themeService = inject(ThemeService);
  private http = inject(HttpClient);

  // OTP state
  emailOtp = '';
  emailOtpSent = false;
  emailVerified = false;
  sendingEmailOtp = false;
  verifyingEmail = false;

  private baseUrl = environment.apiUrl + '/accounts/';

  constructor(
    private authService: AuthService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private toastr: ToasterCommonService,
    private bottomSheet: MatBottomSheet,
    private _cdref: ChangeDetectorRef,
  ) {}

  private emailValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(control.value)) return { invalidEmail: true };
    return null;
  }

  paymentMethods = [
    { value: 'bank_transfer', label: 'Bank Transfer', icon: 'account_balance' },
    { value: 'upi', label: 'UPI', icon: 'smartphone' },
    { value: 'credit_card', label: 'Credit Card', icon: 'credit_card' },
    { value: 'razorpay', label: 'Razorpay', icon: 'payment' },
    { value: 'cheque', label: 'Cheque', icon: 'receipt_long' },
    { value: 'cash', label: 'Cash', icon: 'payments' },
    { value: 'other', label: 'Other', icon: 'more_horiz' },
  ];

  ngOnInit() {
    const reg = '(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?';
    this.signupForm = new FormGroup({
      full_name: new FormControl('', [Validators.required]),
      mobile: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, this.emailValidator.bind(this)]),
      organization: new FormControl('', [Validators.required]),
      website_url: new FormControl('', [Validators.required, Validators.pattern(reg)]),
      subscription_plan: new FormControl('', [Validators.required]),
      payment_method: new FormControl('', [Validators.required]),
      transaction_id: new FormControl('', [Validators.required]),
    });
    this.loadPlans();
  }

  loadPlans() {
    this.http.post<any>(`${this.baseUrl}public/plans/`, {}).subscribe({
      next: (res: any) => { this.plans = res.results || []; },
      error: () => { this.plans = []; },
    });
  }

  onPlanChange() {
    const selectedId = this.signupForm.get('subscription_plan')?.value;
    this.selectedPlan = this.plans.find((p: any) => p.id === selectedId) || null;
  }

  getModuleKeys(modules: any): string[] {
    return modules ? Object.keys(modules) : [];
  }

  // Email OTP
  sendEmailOtp() {
    const email = this.signupForm.get('email')?.value;
    if (!email || this.signupForm.get('email')?.invalid) {
      this.toastr.showError('Please enter a valid email first', '');
      return;
    }
    this.sendingEmailOtp = true;
    this.http.post<any>(`${this.baseUrl}send-otp/`, { type: 'email', recipient: email }).subscribe({
      next: (res: any) => {
        this.sendingEmailOtp = false;
        if (res.success) {
          this.emailOtpSent = true;
          this.toastr.showSuccess(res.message, '');
          this._cdref.detectChanges();
        } else {
          this.toastr.showError(res.message, '');
        }
      },
      error: () => {
        this.sendingEmailOtp = false;
        this.toastr.showError('Failed to send OTP', '');
      },
    });
  }

  verifyEmailOtp() {
    const email = this.signupForm.get('email')?.value;
    if (!this.emailOtp) return;
    this.verifyingEmail = true;
    this.http.post<any>(`${this.baseUrl}verify-otp/`, { type: 'email', recipient: email, otp: this.emailOtp }).subscribe({
      next: (res: any) => {
        this.verifyingEmail = false;
        if (res.success) {
          this.emailVerified = true;
          this.toastr.showSuccess('Email verified!', '');
        } else {
          this.toastr.showError(res.message, '');
        }
      },
      error: () => {
        this.verifyingEmail = false;
        this.toastr.showError('Verification failed', '');
      },
    });
  }

  onEmailPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const trimmed = pastedText.trimStart();
    const control = this.signupForm.get('email');
    control?.setValue(trimmed);
    control?.markAsTouched();
  }

  onEmailInput() {
    const control = this.signupForm.get('email');
    const value = control?.value || '';
    if (value !== value.trimStart()) {
      control?.setValue(value.trimStart(), { emitEvent: false });
    }
  }

  get canSubmit(): boolean {
    return this.signupForm.valid && this.emailVerified;
  }

  signUp(formValue: any) {
    if (!this.emailVerified) {
      this.toastr.showError('Please verify your email before signing up', '');
      return;
    }
    this.loading = true;
    this.authService.createNewUserAndOrganization(formValue).subscribe({
      next: (data) => {
        if (data.status == 201) {
          this.signupForm.reset();
          this.selectedPlan = null;
          this.loading = false;
          this.signupSuccess = true;
        } else {
          this.toastr.showError(data.message, '');
          this.loading = false;
        }
      },
      error: (error) => {
        this.toastr.showError(error, '');
        this.toastr.showError(
          'Your backend server is not working, Please connect with Admin!',
          ''
        );
        this.loading = false;
      },
    });
  }

  signIn() {
    this.router.navigate(['auth', 'sign-in'], { replaceUrl: true });
  }

  goToHome() {
    return window.location.href = 'https://confocean.com';
  }
}
